import { useCallback } from 'react';
import { useHref, useNavigate } from 'react-router-dom';

/**
 * How the application moves to a certificate's own page — as a real address, not a repaint.
 *
 * WHY THIS IS NOT `navigate()`
 * ----------------------------
 * `/c/:code` is the one address the frontend server answers differently: it writes the record's
 * `og:*` tags into that first HTML response, and it is the ONLY response that carries
 * `Referrer-Policy: strict-origin-when-cross-origin` instead of `no-referrer`. Both of those belong
 * to the RESPONSE, so a single-page transition cannot have them: the address bar says `/c/…` while
 * the document is still the one the server sent for `/verify` or `/claim/:token`.
 *
 * That was not cosmetic. Under `no-referrer` Chromium puts `Origin: null` on every cross-site form
 * POST, whatever the form asks for — so the LinkedIn share, whose start is a form POST into the
 * authorization window, presented no usable origin and the API refused it before it looked at the
 * ticket (`start_origin_rejected`, `presentedOrigin: "(none)"`). A recipient who opened their claim
 * link, accepted, and pressed «connect» on the page the application had just shown them could never
 * connect; the same person, opening the same address in a fresh tab, could. Measured in a real
 * browser, over https, against the real `server.mjs`:
 * `tests/production-headers/share-start-origin.spec.ts`.
 *
 * So the browser is handed the address and the server answers it. The cost is one document load on
 * a transition a person makes at most a few times; what it buys is that the page showing a
 * certificate is always the page the server sent for that certificate.
 *
 * THE DEMO IS LEFT ALONE. It runs the same screens under a hash router, where `/c/:code` is a
 * fragment of the page already loaded and there is no server response to go and get — handing that
 * to the browser would navigate out of the demo. `useHref` is the router's own answer to «what URL
 * does this path have here», so a leading `#` is exactly the question being asked.
 */
export function useGoToCredential(): (publicCode: string, search?: string) => void {
  const navigate = useNavigate();
  const probe = useHref('/');
  const hashRouted = probe.startsWith('#');
  return useCallback(
    (publicCode: string, search = '') => {
      const to = `/c/${publicCode}${search}`;
      if (hashRouted) {
        navigate(to);
        return;
      }
      window.location.assign(to);
    },
    [hashRouted, navigate],
  );
}
